// src/components/TopicMafia/components/VoteResultDialog.jsx
import React, { useMemo } from "react";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    Box,
    Divider,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { subActionBtnSx } from "../topicMafiaUi";

function resolveName(playerNameMap, playerId) {
    if (playerId === null || playerId === undefined) return null;
    const pid = Number(playerId);
    if (!Number.isFinite(pid)) return null;

    if (playerNameMap && typeof playerNameMap.get === "function") {
        return playerNameMap.get(pid) || playerNameMap.get(String(pid)) || null;
    }
    if (playerNameMap && typeof playerNameMap === "object") {
        return playerNameMap[pid] || playerNameMap[String(pid)] || null;
    }
    return null;
}

// ✅ 핵심: topIds를 "무조건 배열"로 정규화
function normalizeTopIds(payload) {
    const cand =
        payload?.top_player_ids ??
        payload?.loser_player_ids ??
        payload?.top_ids ??
        payload?.top_players ??
        payload?.top_user_ids ??
        null;

    if (Array.isArray(cand)) return cand.filter((x) => x != null);

    // 단수 키들
    const single = payload?.top_player_id ?? payload?.top_user_id ?? null;
    if (single != null) return [single];

    return [];
}

export default function VoteResultDialog({
    open,
    onClose,
    payload,
    playerNameMap,
    myPlayerId,
    finalLoserPlayerId,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const ui = useMemo(() => {
        if (!payload) return null;

        const topIds = normalizeTopIds(payload);
        const isTie = payload?.is_tie === true;

        const maxVotes = Number(payload?.max_votes);
        const noVotes =
            payload?.no_votes === true || (Number.isFinite(maxVotes) && maxVotes <= 0);

        const round = payload?.round != null ? Number(payload.round) : null;

        const names = topIds.map((pid) => {
            const name = resolveName(playerNameMap, pid) || `#${pid}`;
            return { pid, name };
        });

        let headerSeverity = "success";
        let headerText = "이번 라운드 최다 득표자";

        if (noVotes) {
            headerSeverity = "info";
            headerText = "투표가 없어 결과 없음";
        } else if (isTie) {
            headerSeverity = "warning";
            headerText = "동점! 최다 득표자";
        }

        return { topIds, isTie, maxVotes, noVotes, round, names, headerSeverity, headerText };
    }, [payload, playerNameMap]);

    const renderNameChips = () => {
        if (!ui) return null;
        if (ui.noVotes) return null;
        if (!ui.names.length) return null;

        return (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {ui.names.map(({ pid, name }) => {
                    const isMe = myPlayerId != null && Number(pid) === Number(myPlayerId);
                    const isFinal = finalLoserPlayerId != null && Number(pid) === Number(finalLoserPlayerId);

                    return (
                        <Chip
                            key={String(pid)}
                            label={isFinal ? `🎯 ${name}` : isMe ? `나 · ${name}` : name}
                            variant={isFinal ? "filled" : "outlined"}
                            sx={{
                                fontWeight: 800,
                                borderRadius: 2,
                                ...(isMe && { boxShadow: "0 0 0 2px rgba(0,0,0,0.08) inset" }),
                            }}
                        />
                    );
                })}
            </Stack>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            fullWidth
            maxWidth={isMobile ? false : "xs"}
        >
            <DialogTitle sx={{ fontWeight: 900 }}>🗳️ 투표 결과</DialogTitle>

            <DialogContent dividers>
                {!ui ? (
                    <Typography sx={{ color: "text.secondary" }}>결과를 불러오는 중…</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {ui.round != null && <Chip label={`라운드 ${ui.round}`} />}
                            {Number.isFinite(ui.maxVotes) && !ui.noVotes && <Chip label={`${ui.maxVotes}표`} />}
                            {ui.isTie && !ui.noVotes && <Chip label="동점" color="warning" />}
                            {ui.noVotes && <Chip label="무투표" color="info" />}
                        </Stack>

                        <Alert severity={ui.headerSeverity} sx={{ borderRadius: 2 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography sx={{ fontWeight: 900 }}>{ui.headerText}</Typography>

                                {ui.noVotes ? (
                                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        이번 라운드는 투표가 없어 넘어갑니다.
                                    </Typography>
                                ) : ui.names.length === 0 ? (
                                    <>
                                        {console.warn("VoteResultDialog: topIds empty. payload=", payload)}
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            최다 득표자 정보를 찾지 못했어.
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Divider sx={{ my: 0.5 }} />
                                        {renderNameChips()}
                                    </>
                                )}
                            </Box>
                        </Alert>

                        {payload?.summary && (
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {payload.summary}
                            </Typography>
                        )}

                        {payload?.phase && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                상태: {String(payload.phase)}
                            </Typography>
                        )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions
                sx={
                    isMobile
                        ? { position: "sticky", bottom: 0, bgcolor: "background.paper", zIndex: 1 }
                        : undefined
                }
            >
                <Button onClick={onClose} sx={subActionBtnSx} fullWidth={isMobile}>
                    닫기
                </Button>
            </DialogActions>
        </Dialog>
    );
}
